import { useAuth } from "@clerk/clerk-react";
import { Box, Grid, TextField, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { ProfileType } from "schemas/dashboard";
import { CustomEvent, Map } from "schemas/functions";

import LocationAutocomplete from "components/LocationAutocomplete";
import { UserLocation } from "components/LocationAutocomplete/schema";
import StyledAutocomplete from "components/Styled/Autocomplete";
import StyledTextField from "components/Styled/TextField";
import { fetcherAuth } from "utils/api";
import { hasSocialMediaProfile } from "utils/influencer";
import { makeDeepCopy } from "utils/updateLocalState";

import AboutFormLinks from "../../AboutForm/Links";
import {
  SocialMediaFields,
  SocialMediaLinks,
  isValidUsername,
  sanitizeInput,
} from "../../AboutForm/helpers";
import StyledProPage from "../StyledPage";

interface ProOnboardingAboutFormProps {
  handleNext: (onboardingForm?: Map) => void;
  handleBack?: () => void;
  onboardingForm: Map;
  setOnboardingForm: Dispatch<SetStateAction<Map>>;
  onFinishSetUpLater?: () => void;
}

const ProOnboardingAboutForm = ({
  handleNext,
  handleBack,
  onboardingForm,
  setOnboardingForm,
  onFinishSetUpLater,
}: ProOnboardingAboutFormProps) => {
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const [linksValidation, setLinksValidation] = useState<{
    [key in string]: string | undefined;
  }>({});
  const [links, setLinks] = useState<SocialMediaLinks[]>([]);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState<number>(-1);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const handleChange = (e: CustomEvent) => {
    const { name, value } = e.target;
    const copy = makeDeepCopy(onboardingForm);
    copy[name] = value;
    setOnboardingForm(copy);
  };

  const handleLocationChange = (userLocation: UserLocation) => {
    setOnboardingForm((prev) => ({
      ...prev,
      city: userLocation.address,
      location: userLocation.country_code,
      addressComponents: userLocation.address_components,
      longitude: userLocation.longitude,
      latitude: userLocation.latitude,
    }));
  };

  const handleSubmit = async () => {
    if (Object.values(linksValidation).some((v) => v !== undefined)) {
      return;
    }

    for (const key in onboardingForm) {
      if (key in SocialMediaFields && onboardingForm[key]) {
        const input = sanitizeInput(onboardingForm[key]);
        if (input && !isValidUsername(input) && key !== "portfolioUrl") {
          return;
        }
      }
    }

    if (!onboardingForm?.name) {
      setError("Please fill in your name to continue");
      return;
    }

    if (
      onboardingForm?.latitude &&
      onboardingForm?.longitude &&
      onboardingForm?.latitude === onboardingForm?.longitude
    ) {
      setError(
        "Cannot store this location, please retype the address and try again",
      );
      setOnboardingForm((prev) => ({
        ...prev,
        latitude: undefined,
        longitude: undefined,
      }));
      return;
    }

    if (!hasSocialMediaProfile(onboardingForm)) {
      setError("Please provide at least one social media profile to continue");
      return;
    }

    setLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding`,
        "PUT",
        {},
        {
          ...onboardingForm,
        },
      );
      handleNext(onboardingForm);
      setLoading(false);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const setActiveQuestionAutomatically = () => {
    if (!onboardingForm?.name) {
      setActiveQuestionIndex(0);
      return;
    }
    const locationCompleted =
      !!onboardingForm?.addressComponents &&
      onboardingForm?.addressComponents.length >= 2 &&
      !!onboardingForm?.location;
    if (!locationCompleted) {
      setActiveQuestionIndex(1);
      return;
    }
    if (
      !onboardingForm?.profileTypes ||
      onboardingForm?.profileTypes.length === 0
    ) {
      setActiveQuestionIndex(2);
      return;
    }

    if (!hasSocialMediaProfile(onboardingForm)) {
      setActiveQuestionIndex(3);
      return;
    }
    setActiveQuestionIndex(-1);
  };

  useEffect(() => {
    setActiveQuestionAutomatically();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    Boolean(onboardingForm?.name),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    Boolean(onboardingForm?.addressComponents),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    Boolean(
      onboardingForm?.profileTypes && onboardingForm?.profileTypes.length > 0,
    ),
  ]);

  return (
    <StyledProPage
      title={"Let's Get To Know You ✍️"}
      onBack={handleBack}
      onNext={handleSubmit}
      onFinishSetUpLater={onFinishSetUpLater}
      stepNumber={1}
      description={
        "This will help us personalize your experience and connect you with the right opportunities."
      }
      buttonNext={"Continue to Templates"}
      buttonBack={"Back"}
      loading={loading}
      body={
        <Grid container gap={2} flexDirection={"column"}>
          <Grid item xs={12}>
            <StyledTextField
              fullWidth
              name="name"
              placeholder="First Last"
              onChange={handleChange}
              required
              value={onboardingForm?.name || ""}
              completed={!!onboardingForm?.name}
              label="Your Name"
              allowCollapsible
              isActive={activeQuestionIndex === 0}
              error={error}
            />
          </Grid>
          <Grid item xs={12}>
            <LocationAutocomplete
              handleLocationChange={handleLocationChange}
              userLocation={{
                address: onboardingForm.city,
                longitude: onboardingForm.longitude,
                latitude: onboardingForm.latitude,
                country_code: onboardingForm.location,
                address_components: onboardingForm.addressComponents,
              }}
              customStyling={{
                height: 52,
                width: "100%",
                borderRadius: 4,
                border: `1px solid ${grey[300]}`,
                px: 1,

                "&:hover, &:focus": {
                  outline: "none",
                },
              }}
              label="City or Zip Code"
              required
              allowCollapsible
              completed={
                !!onboardingForm?.addressComponents &&
                onboardingForm?.addressComponents.length >= 2 &&
                !!onboardingForm?.location
              }
              isActive={activeQuestionIndex === 1}
            />

            {error && activeQuestionIndex === 1 && (
              <Typography sx={{ color: "error.main" }}>{error}</Typography>
            )}
          </Grid>

          <Box>
            <StyledAutocomplete
              required
              label="Who are you?"
              multiple
              options={Object.values(ProfileType)}
              value={onboardingForm?.profileTypes || []}
              onChange={(_: any, newValue: ProfileType[]) => {
                setOnboardingForm((p) => {
                  if (!p) return p;
                  const copy = { ...p };
                  copy.profileTypes = newValue;
                  return copy;
                });
              }}
              completed={
                onboardingForm?.profileTypes &&
                onboardingForm?.profileTypes?.length > 0
              }
              isActive={activeQuestionIndex === 2}
              error={activeQuestionIndex === 2 && error ? error : ""}
            />
            {onboardingForm?.profileTypes?.includes(ProfileType.other) && (
              <TextField
                placeholder="Please specify"
                value={onboardingForm?.otherType || ""}
                onChange={(e: CustomEvent) => {
                  const { value } = e.target;

                  setOnboardingForm((p) => {
                    if (!p) return p;
                    const copy = { ...p };
                    copy.otherType = value;
                    return copy;
                  });
                }}
                variant="standard"
                sx={{ mt: 1 }}
              />
            )}
          </Box>
          <AboutFormLinks
            onboardingForm={onboardingForm}
            setOnboardingForm={setOnboardingForm}
            setLinksValidation={setLinksValidation}
            links={links}
            setLinks={setLinks}
            validation={linksValidation}
            activeQuestionIndex={activeQuestionIndex}
            error={error}
          />
        </Grid>
      }
    />
  );
};

export default ProOnboardingAboutForm;
